import { useNavigate, useLocation, Link } from "react-router-dom";
import {
  BookOpen,
  Lightbulb,
  LogOut,
  LogIn,
} from "lucide-react";

import { useCurrentUser } from "../hooks/useCurrentUser";
import { useAuth } from "../hooks/useAuth";
import { showConfirm } from "../utils/confirm";
import { ROUTES } from "../config/constants";

export default function Portal() {
  const navigate = useNavigate();
  const location = useLocation();
  const { userName } = useCurrentUser();
  const { forceLogout } = useAuth();

  // Detect if this is the public portal (/dashboard) or internal portal (/)
  const isPublic = location.pathname === ROUTES.PUBLIC_PORTAL;

  function handleLogout() {
    showConfirm({
      title: "Konfirmasi Logout",
      message: "Apakah Anda yakin ingin keluar dari aplikasi?",
      confirmLabel: "Ya, Logout",
      cancelLabel: "Batal",
      onConfirm: () => {
        forceLogout();
        navigate(ROUTES.LOGIN);
      },
    });
  }

  function handletoportal() {
    navigate(ROUTES.PORTAL);
  }

  const cards = [
    {
      title: "Lesson Learned",
      desc:
        "Dokumentasi pembelajaran proyek untuk mencegah masalah berulang dan meningkatkan kualitas.",
      icon: <BookOpen size={34} className="text-blue-500" />,
      glow: "from-blue-500/10 to-cyan-400/5",
      border: "border-blue-100",
      bgIcon: "bg-blue-50",
      link: isPublic
        ? ROUTES.LESSONS_DASHBOARD
        : ROUTES.LESSONS,
    },
    {
      title: "VA / VE",
      desc:
        "Value Analysis / Value Engineering untuk efisiensi biaya serta peningkatan mutu.",
      icon: <Lightbulb size={34} className="text-amber-500" />,
      glow: "from-amber-500/10 to-orange-400/5",
      border: "border-amber-100",
      bgIcon: "bg-amber-50",
      link: isPublic
        ? ROUTES.VAVE_DASHBOARD
        : ROUTES.VAVE,
    },
  ];

  return (
    <div className="relative min-h-screen overflow-hidden bg-white px-6 py-8 md:px-10">
      {/* Background Glow */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-[-120px] left-[-80px] w-[380px] h-[380px] bg-blue-200/40 rounded-full blur-3xl animate-pulse" />

        <div
          className="absolute bottom-[-120px] right-[-80px] w-[420px] h-[420px] bg-amber-200/40 rounded-full blur-3xl animate-pulse"
          style={{ animationDuration: "6s" }}
        />

        <div
          className="absolute top-[40%] left-[45%] w-[260px] h-[260px] bg-cyan-200/30 rounded-full blur-3xl animate-pulse"
          style={{ animationDuration: "8s" }}
        />

        {/* Grid Effect */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(0,0,0,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,0.03)_1px,transparent_1px)] bg-[size:50px_50px]" />
      </div>

      <div className="relative z-10 w-full max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5 mb-12">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-gray-200 bg-white/70 backdrop-blur-md mb-4 shadow-sm">
              <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />

              <span className="text-[11px] tracking-wider text-gray-600 uppercase">
                Knowledge Management
              </span>
            </div>

            <h1 className="text-2xl md:text-4xl font-semibold text-gray-900 leading-tight">
              {isPublic
                ? "Dashboard Knowledge"
                : "Portal Knowledge"}
            </h1>

            {isPublic ? (
              <p className="text-gray-500 mt-3 text-sm max-w-2xl">
                Portal publik untuk melihat data Lesson Learned
                dan VA / VE proyek WIKA.
              </p>
            ) : (
              <p className="text-gray-500 mt-3 text-sm">
                Selamat datang kembali,{" "}
                <span className="text-gray-900 font-medium">
                  {userName}
                </span>
              </p>
            )}
          </div>

          {/* Login / Portal Button */}
          {isPublic ? (
            <Link
              to={ROUTES.LOGIN}
              className="group flex items-center gap-2 px-4 py-2.5 text-sm text-blue-600 bg-white/80 border border-blue-100 backdrop-blur-xl rounded-2xl hover:bg-blue-50 transition-all duration-300 shadow-sm"
            >
              <LogIn
                size={16}
                className="group-hover:-translate-y-0.5 transition-transform"
              />

              <span>Login</span>
            </Link>
          ) : (
            <button
              onClick={handletoportal}
              className="group flex items-center gap-2 px-4 py-2.5 text-sm text-blue-600 bg-white/80 border border-blue-100 backdrop-blur-xl rounded-2xl hover:bg-blue-50 transition-all duration-300 shadow-sm"
            >
              <LogOut
                size={16}
                className="group-hover:-translate-x-0.5 transition-transform"
              />

              <span>Portal</span>
            </button>
          )}
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {cards.map((item, index) => (
            <Link
              key={index}
              to={item.link}
              className={`group relative overflow-hidden rounded-3xl border ${item.border} bg-white/70 backdrop-blur-xl p-7 min-h-[300px] transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl shadow-lg`}
            >
              {/* Glow Hover */}
              <div
                className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition duration-500 bg-gradient-to-br ${item.glow}`}
              />

              {/* Overlay */}
              <div className="absolute inset-0 bg-white/40" />

              <div className="relative z-10 flex flex-col h-full">
                {/* Icon */}
                <div
                  className={`w-16 h-16 rounded-2xl ${item.bgIcon} border border-white flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-500 shadow-sm`}
                >
                  {item.icon}
                </div>

                {/* Title */}
                <h2 className="text-xl font-semibold text-gray-900 mb-3">
                  {item.title}
                </h2>

                {/* Desc */}
                <p className="text-sm text-gray-500 leading-relaxed">
                  {item.desc}
                </p>

                {/* Bottom */}
                <div className="mt-auto pt-8">
                  <div className="inline-flex items-center gap-2 text-xs text-gray-600 group-hover:text-gray-900 transition">
                    <span>Open Module</span>

                    <div className="w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center group-hover:translate-x-1 transition-transform">
                      →
                    </div>
                  </div>
                </div>
              </div>

              {/* Border Glow */}
              <div className="absolute inset-0 rounded-3xl border border-white/40 group-hover:border-gray-200 transition-all duration-500" />
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
